import { Notice } from '@homecare/ui';
import type { ReactNode } from 'react';

import { ApiRequestError } from '../lib/api';

/**
 * What the console adds to the kit, and nothing more.
 *
 * Button, Field, SelectField, Card, EmptyState, StatusPill, DataTable, Modal
 * and Toast all come from `@homecare/ui` — importing them through here as well
 * would give the console a second place to change how a button looks, which is
 * the same failure as a second colour ramp.
 *
 * Only `ErrorNote` lives here, because it is the one piece that knows about
 * this app's transport: it reads the §26.2 error envelope off an
 * `ApiRequestError`, which the design system has no business knowing exists.
 */

/**
 * A failed request, in the words of the person reading it.
 *
 * Renders `userMessage` from the envelope, and the field-level `details` under
 * it. Never a status code, an error code or a stack — §12: never expose
 * internals. The next step is the caller's to supply, because only the caller
 * knows what the person was trying to do when it failed.
 *
 * ## Why `details` is rendered here
 *
 * §26.2 puts field-level messages in `details`, and for a long time nothing in
 * the console read them. That was invisible while every refusal the console
 * could provoke had a self-sufficient `userMessage` — and stopped being
 * invisible the moment a refusal's whole value was in the detail.
 *
 * §17.3's ACTIVE gate is that refusal. The catalogue's `VALIDATION_FAILED`
 * message is "Some of what you entered is not valid", which is true and
 * useless: the useful sentence is the machine's own, naming the onboarding
 * items still outstanding, and it arrives in `details`. Dropping it turned a
 * refusal that says exactly what to do into one that says nothing.
 *
 * The keys are deliberately not rendered. `toStatus` is a field name, and §12
 * says a person never reads one.
 */
export function ErrorNote({
  error,
  nextStep,
}: {
  error: unknown;
  nextStep?: ReactNode;
}): ReactNode {
  if (!error) return null;

  const userMessage =
    error instanceof ApiRequestError
      ? error.userMessage
      : 'Something on our side did not respond. Try again in a moment.';

  const details =
    error instanceof ApiRequestError
      ? Object.values(error.details)
          .flat()
          .filter((line) => line.trim().length > 0)
      : [];

  return (
    <Notice tone="critical" title={userMessage}>
      {details.length > 0 && (
        <ul className="grid gap-1">
          {details.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {nextStep}
    </Notice>
  );
}
